import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControlLabel, IconButton, LinearProgress, MenuItem,
  Paper, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublishIcon from '@mui/icons-material/Publish';
import UndoIcon from '@mui/icons-material/Undo';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  archiveEmailTemplate, canUsePurpose, createEmailTemplate, describeUsage, formatBytes, formatWhen,
  getEmailTemplate, getEmailTemplateVersion, previewEmailTemplate, publishEmailTemplate,
  reconcileEmailTemplate, rollbackEmailTemplate, sendTemplateTest, statusLabel, syncLabel,
  toTemplateApiError, utf8Bytes, PURPOSE_LABEL, STATUS_COLOR, VERSION_SOURCE_LABEL,
  type PreviewResult, type Purpose, type RenderedPreview, type SetupState, type TemplateApiError,
  type TemplateCatalog, type TemplateDetail, type TemplateDraft, type TemplateDrift,
  type TemplateIssue, type TemplatePublic, type TemplateWriteResult, type VersionDetail, type VersionSummary,
} from '../services/email-templates-api';

/**
 * The editor for one SIMS-managed SendGrid template: write it, preview it with synthetic data,
 * publish it, roll it back, and test it on yourself.
 *
 * Every publish is a NEW SendGrid version, activated; the one it replaces stays for rollback.
 * Before any write the server compares SendGrid with what SIMS last saw. A console edit is saved
 * into history and the write answers 409 drift, and only an explicit "Publish over it" (which names
 * exactly that console version) goes ahead. A SendGrid timeout is neither success nor failure:
 * "may be live", settled by Check now.
 *
 * The preview is the server's: the same subset renderer that refuses anything SendGrid would
 * render differently. It is shown in an iframe with sandbox="" (no scripts, no same-origin, no
 * popups) and the server's CSP document, never through innerHTML.
 *
 * Layout tiers (standing rule - separate views, never one fluid rule): the base styles are the
 * laptop, one column with the fields above the preview. From 1800px the editor and the preview sit
 * side by side. From 2400px (the 34-inch ultrawide) a third column holds the placeholders and the
 * history. Rules inside a media query never reach the laptop. The dialog is full screen because
 * the tiers are viewport queries; a 900px dialog would squeeze two columns into a laptop's width.
 *
 * Campaign templates have had no screen since 2026-10-06 (staff create them in SendGrid), so today
 * this editor only ever opens for the drip layout and its purpose === 'campaign' branches cannot run.
 * They stay only to mirror the backend's purposes, until a follow-up retires the campaign purpose in
 * frontend and backend together.
 */

const WIDE = '@media (min-width: 1800px)';
const ULTRAWIDE = '@media (min-width: 2400px)';

// A grid or flex child refuses to shrink below its content unless told to; without this a long
// line of HTML pushes the whole dialog sideways.
const SHRINKS = { minWidth: 0 };

const LAYOUT = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"catalog" "editor" "preview" "history"',
  gap: 2,
  alignItems: 'start',
  [WIDE]: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gridTemplateAreas: '"catalog catalog" "editor preview" "editor history"',
    gap: 3,
  },
  [ULTRAWIDE]: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr) 360px',
    gridTemplateAreas: '"editor preview catalog" "editor preview history"',
    gridTemplateRows: 'auto 1fr',
    gap: 4,
  },
};

// A sandbox="" srcDoc iframe cannot size itself (it would be 150px tall), so every tier sets one.
const FRAME_HEIGHT = {
  height: 560,
  [WIDE]: { height: 'max(620px, calc(100vh - 360px))' },
  [ULTRAWIDE]: { height: 'max(760px, calc(100vh - 320px))' },
};

const HTML_AREA = {
  '& textarea': { height: 360, resize: 'vertical' },
  [WIDE]: { '& textarea': { height: 'max(420px, calc(100vh - 600px))' } },
  [ULTRAWIDE]: { '& textarea': { height: 'max(520px, calc(100vh - 560px))' } },
};

const PLAIN_AREA = {
  '& textarea': { height: 160, resize: 'vertical' },
  [ULTRAWIDE]: { '& textarea': { height: 220 } },
};

// Chips wrap on the laptop and the wide screen; the ultrawide sidebar has room for a description.
const KEY_LIST = {
  display: 'flex', flexWrap: 'wrap', gap: 1,
  [ULTRAWIDE]: { flexDirection: 'column', flexWrap: 'nowrap', gap: 1.25 },
};
const KEY_ROW = { ...SHRINKS, display: 'flex', alignItems: 'center', gap: 1 };
const KEY_DESC = {
  display: 'none', color: 'var(--text-secondary)', overflowWrap: 'anywhere',
  [ULTRAWIDE]: { display: 'block' },
};

const MONO = { fontFamily: 'Consolas, "Courier New", monospace' };

// The live preview posts the whole document after each pause in typing, and the Flex worker serves
// one request at a time: checking 300 KB takes it most of a second, during which other staff wait.
// Above this size only the Check & preview button runs it.
const AUTO_PREVIEW_MAX_BYTES = 64 * 1024;

type Field = 'subject' | 'html' | 'plain';
type WriteKind = 'create' | 'publish' | 'rollback';
type Busy = WriteKind | 'reconcile' | 'archive' | 'test' | null;

interface WriteOpts {
  confirmLive?: boolean;
  overwriteDrift?: { sgVersionId: string | null; sgUpdatedAt: string | null };
  confirmTargetEdited?: { sgVersionId: string | null; sgUpdatedAt: string | null };
}

interface ConfirmState {
  kind: WriteKind | 'archive' | 'replace';
  opts?: WriteOpts;
  title: string;
  lines: string[];
  confirmLabel: string;
  /** For 'replace': what to load into the editor once confirmed. */
  content?: Content;
}

interface Notice {
  severity: 'success' | 'info' | 'warning' | 'error';
  text: string;
  action?: 'reconcile' | 'reload';
}

interface Content { subject: string; html: string; plain: string }

const CONFIRM_LIVE_CAMPAIGN = 'Emails sent through SendGrid with this template from now on use this version. '
  + 'Resends from the campaign page do not: they send the copy saved when each campaign was created.';
const CONFIRM_LIVE_DRIP = 'Drip emails sent from now on use this version of the layout.';
const ADOPTED_NOTE = 'This template was adopted from SendGrid. Publishing changes it for every system that '
  + 'sends with it, not only SIMS, and the first SIMS publish uses the code editor, so design-editor '
  + 'modules are dropped. Roll back restores exactly what was live before.';
const RESIDUAL_NOTE = 'The preview uses SIMS\'s copy of SendGrid\'s rules. The exact spelling of entities '
  + 'for \' and " and comparisons of numbers with text can differ; a live test send is what SendGrid really sends.';

function liveMessage(purpose: Purpose): string {
  return purpose === 'campaign' ? CONFIRM_LIVE_CAMPAIGN : CONFIRM_LIVE_DRIP;
}

function issueText(i: TemplateIssue): string {
  const where = [i.context && i.context !== i.name ? i.context : null, i.line ? `line ${i.line}` : null]
    .filter(Boolean).join(', ');
  return `${i.name ? `${i.name}: ` : ''}${i.message}${where ? ` (${where})` : ''}`;
}

function copy(text: string | null | undefined) {
  if (!text) return;
  try { void navigator.clipboard?.writeText(text); } catch { /* clipboard blocked: nothing to do */ }
}

// ── an uncontrolled code field ──────────────────────────────────────────────────────────────────

export interface CodeAreaHandle {
  getValue: () => string;
  setValue: (value: string) => void;
  insertAtCursor: (text: string) => void;
}

/**
 * Up to 300 KB of HTML. A controlled field re-renders the dialog on every keystroke and MUI's
 * autosize re-measures the whole value each time, so this is a plain, uncontrolled textarea and the
 * parent hears about changes a moment after typing stops. Anything that must be exact (publish,
 * preview, test send) reads getValue(), never the debounced copy.
 */
const CodeArea = forwardRef<CodeAreaHandle, {
  label: string;
  helperText?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  sx?: object;
  onChange: (value: string) => void;
  onFocus?: () => void;
}>(function CodeArea({ label, helperText, placeholder, disabled, sx, onChange, onFocus }, ref) {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useImperativeHandle(ref, () => ({
    getValue: () => inputRef.current?.value ?? '',
    setValue: (value: string) => {
      window.clearTimeout(timer.current);
      if (inputRef.current) inputRef.current.value = value;
      latest.current(value);
    },
    insertAtCursor: (text: string) => {
      const el = inputRef.current;
      if (!el) return;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? start;
      el.setRangeText(text, start, end, 'end');
      el.focus();
      latest.current(el.value);
    },
  }), []);

  return (
    <TextField
      label={label} helperText={helperText} placeholder={placeholder} disabled={disabled}
      multiline rows={12} fullWidth defaultValue=""
      inputRef={inputRef}
      InputLabelProps={{ shrink: true }}
      // A plain textarea rather than MUI's autosize one (see above).
      InputProps={{ inputComponent: 'textarea' }}
      inputProps={{ spellCheck: false, autoCapitalize: 'off', autoCorrect: 'off', style: { ...MONO, fontSize: 13 } }}
      onFocus={onFocus}
      onChange={() => {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => latest.current(inputRef.current?.value ?? ''), 300);
      }}
      sx={sx}
    />
  );
});

// ── the editor ──────────────────────────────────────────────────────────────────────────────────

export default function EmailTemplateEditor({
  open, templateId, initialPurpose, catalog, setup, roles, callerEmail, onClose, onChanged,
}: {
  open: boolean;
  /** null = writing a new template */
  templateId: string | null;
  /** The purpose a new template starts with. */
  initialPurpose: Purpose;
  catalog: TemplateCatalog | null;
  setup: SetupState;
  roles: string[];
  /** Shown on the test-send button. The server sends to the VERIFIED token address, not this. */
  callerEmail: string;
  onClose: () => void;
  /** Something was written; the list should reload. */
  onChanged: (message: string) => void;
}) {
  const [currentId, setCurrentId] = useState<string | null>(templateId);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [contentReady, setContentReady] = useState(false);
  const [contentNote, setContentNote] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState<Purpose>(initialPurpose);
  const [subject, setSubject] = useState('');
  const [htmlText, setHtmlText] = useState('');
  const [plainText, setPlainText] = useState('');
  const [baseline, setBaseline] = useState<Content>({ subject: '', html: '', plain: '' });
  const [lastField, setLastField] = useState<Field>('html');

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [rendered, setRendered] = useState<RenderedPreview | null>(null);
  // True while the frame shows the stored live version, before the first fresh preview.
  const [renderedFromStore, setRenderedFromStore] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [autoPreview, setAutoPreview] = useState(true);
  const [frameWidth, setFrameWidth] = useState<620 | 375>(620);
  const [loadImages, setLoadImages] = useState(true);

  const [busy, setBusy] = useState<Busy>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [writeErrors, setWriteErrors] = useState<TemplateIssue[]>([]);
  const [writeWarnings, setWriteWarnings] = useState<TemplateIssue[]>([]);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [keyMissing, setKeyMissing] = useState(false);

  const [testMode, setTestMode] = useState<'draft' | 'live'>('draft');
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const [versionView, setVersionView] = useState<VersionDetail | null>(null);
  const [versionLoading, setVersionLoading] = useState<number | null>(null);
  const [showRules, setShowRules] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const htmlRef = useRef<CodeAreaHandle | null>(null);
  const plainRef = useRef<CodeAreaHandle | null>(null);
  const subjectInputRef = useRef<HTMLInputElement | null>(null);
  const etagRef = useRef<string | null>(null);
  const purposeRef = useRef<Purpose>(purpose);
  const subjectRef = useRef(subject);
  const loadImagesRef = useRef(loadImages);
  const previewInFlight = useRef(false);
  const previewAgain = useRef(false);
  const loadSeq = useRef(0);
  // Bumped each time the dialog opens, so a preview still in flight for the previous template can
  // never land in this one.
  const openGen = useRef(0);
  purposeRef.current = purpose;
  subjectRef.current = subject;
  loadImagesRef.current = loadImages;

  const template = detail?.template ?? null;
  const creating = !currentId;
  const purposeCatalog = catalog?.purposes?.[purpose] ?? null;
  const limits = purposeCatalog?.limits;
  const mayUse = canUsePurpose(roles, purpose);
  const keyReady = setup.templatesKeyConfigured && !keyMissing;
  const canWrite = mayUse && keyReady && !busy;
  const pendingOp = template?.pendingOp ?? null;
  const archived = template?.status === 'archived';
  const htmlBytes = utf8Bytes(htmlText);
  const plainBytes = utf8Bytes(plainText);
  const dirtyNow = () =>
    subjectRef.current !== baseline.subject
    || (htmlRef.current?.getValue() ?? htmlText) !== baseline.html
    || (purposeRef.current === 'campaign' && (plainRef.current?.getValue() ?? plainText) !== baseline.plain)
    || (creating && !!name.trim());

  // ── content ───────────────────────────────────────────────────────────────────────────────────

  const putContent = (c: Content, asBaseline = true) => {
    setSubject(c.subject);
    htmlRef.current?.setValue(c.html);
    plainRef.current?.setValue(c.plain);
    if (asBaseline) setBaseline(c);
    setContentReady(true);
  };

  const currentDraft = (): TemplateDraft => {
    const p = purposeRef.current;
    const plain = p === 'campaign' ? (plainRef.current?.getValue() ?? '') : '';
    return {
      purpose: p, subject: subjectRef.current, html: htmlRef.current?.getValue() ?? '',
      // Blank plain text means "generate it from the HTML"; a drip layout's is always generated.
      ...(p === 'campaign' && plain.trim() ? { plain } : {}),
    };
  };

  /** The live version's content. For a campaign, a plain part SIMS generated loads as blank so it
   *  keeps being generated from the HTML - loading it as typed text would freeze a stale copy. */
  const contentFromDetail = async (d: TemplateDetail): Promise<Content> => {
    const av = d.activeVersion;
    if (!av) {
      setContentNote(d.template.status === 'draft'
        ? 'This template has no live version yet. Write it below and publish.' : null);
      return { subject: '', html: '', plain: '' };
    }
    if (!av.contentStored) {
      setContentNote('The live version was too large for SIMS to keep a copy, so the editor is empty. '
        + 'It is still live in SendGrid; publishing replaces it.');
      return { subject: av.subject || '', html: '', plain: '' };
    }
    let plain = '';
    if (d.template.purpose === 'campaign' && av.versionNo != null) {
      try {
        const v = await getEmailTemplateVersion(d.template.id, av.versionNo);
        plain = v.plainGenerated ? '' : (v.plain || '');
      } catch {
        setContentNote('The saved plain text could not be loaded, so its box is blank. SIMS generates the '
          + 'plain text from the HTML unless you write one.');
      }
    }
    return { subject: av.subject || '', html: av.html || '', plain };
  };

  const loadDetail = useCallback(async (id: string, withContent: boolean) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const d = await getEmailTemplate(id);
      if (seq !== loadSeq.current) return null;
      setDetail(d);
      setPurpose(d.template.purpose);
      purposeRef.current = d.template.purpose;
      etagRef.current = d.template.etag;
      if (withContent) {
        setContentNote(null);
        const c = await contentFromDetail(d);
        if (seq !== loadSeq.current) return null;
        putContent(c);
        if (d.activeVersion?.previewDocument) {
          setRendered({ subject: d.activeVersion.subject || '', html: '', plain: '',
            previewDocument: d.activeVersion.previewDocument });
          setRenderedFromStore(true);
        }
      }
      return d;
    } catch (e) {
      if (seq === loadSeq.current) {
        setLoadError(toTemplateApiError(e, 'The template could not be loaded.').error);
      }
      return null;
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!open) return;
    openGen.current += 1;
    loadSeq.current += 1;
    previewAgain.current = false;
    setCurrentId(templateId);
    setDetail(null);
    setSubject('');
    subjectRef.current = '';
    setHtmlText('');
    setPlainText('');
    setBaseline({ subject: '', html: '', plain: '' });
    setLoadError(null);
    setContentNote(null);
    setName('');
    setPurpose(initialPurpose);
    purposeRef.current = initialPurpose;
    setPreview(null);
    setRendered(null);
    setRenderedFromStore(false);
    setPreviewError(null);
    setNotice(null);
    setWriteErrors([]);
    setWriteWarnings([]);
    setConfirm(null);
    setKeyMissing(false);
    setTestMode('draft');
    setTestResult(null);
    setVersionView(null);
    setLastField('html');
    etagRef.current = null;
    if (templateId) {
      setContentReady(false);
      // The fields mount with the dialog; give them a frame before content is put into them.
      window.setTimeout(() => { void loadDetail(templateId, true); }, 0);
    } else {
      window.setTimeout(() => putContent({ subject: '', html: '', plain: '' }), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  // ── preview ───────────────────────────────────────────────────────────────────────────────────

  /** explicit: the button was pressed, so an empty editor deserves an answer, not silence. */
  const runPreview = async (explicit = false) => {
    if (previewInFlight.current) { previewAgain.current = true; return; }
    const draft = currentDraft();
    if (!draft.html.trim()) {
      setPreview(null);
      setPreviewError(explicit ? 'Write the HTML first; the preview needs it.' : null);
      return;
    }
    previewInFlight.current = true;
    setPreviewing(true);
    const gen = openGen.current;
    try {
      const r = await previewEmailTemplate({ ...draft, loadImages: loadImagesRef.current });
      if (gen !== openGen.current) return;
      setPreview(r);
      if (r.rendered) { setRendered(r.rendered); setRenderedFromStore(false); }
      setPreviewError(null);
    } catch (e) {
      if (gen === openGen.current) {
        setPreviewError(toTemplateApiError(e, 'The preview could not be produced.').error);
      }
    } finally {
      previewInFlight.current = false;
      setPreviewing(false);
      if (previewAgain.current) {
        previewAgain.current = false;
        void runPreview();
      }
    }
  };

  // Live preview: a moment after typing stops, one request at a time. The preview route does no
  // writes and calls nothing external; above AUTO_PREVIEW_MAX_BYTES it waits for the button.
  const autoPreviewPaused = autoPreview && htmlBytes > AUTO_PREVIEW_MAX_BYTES;
  useEffect(() => {
    if (!open || !contentReady || !autoPreview || autoPreviewPaused) return;
    const t = window.setTimeout(() => { void runPreview(); }, 1200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contentReady, autoPreview, autoPreviewPaused, subject, htmlText, plainText, purpose, loadImages]);

  // ── writes ────────────────────────────────────────────────────────────────────────────────────

  const reload = async (withContent = false) => {
    if (currentId) await loadDetail(currentId, withContent);
  };

  const confirmLinesForUsage = (p: Purpose, usageText: string): string[] => [usageText, liveMessage(p)];

  const handleWriteError = async (kind: WriteKind, opts: WriteOpts, err: TemplateApiError) => {
    const p = purposeRef.current;
    switch (err.code) {
      case 'invalid':
        setWriteErrors(Array.isArray(err.body.errors) ? err.body.errors : []);
        setWriteWarnings(Array.isArray(err.body.warnings) ? err.body.warnings : []);
        setNotice({ severity: 'error', text: err.error });
        return;
      case 'name_taken':
        setWriteErrors(Array.isArray(err.body.errors) && err.body.errors.length
          ? err.body.errors : [{ code: 'name_taken', name: 'name', message: err.error }]);
        setNotice({ severity: 'error', text: err.error });
        return;
      case 'confirm_live':
        // Usage appeared since this editor loaded it; the server's counts are the current ones.
        setConfirm({
          kind, opts: { ...opts, confirmLive: true },
          title: 'This template is in use',
          lines: [describeUsage(err.body.usage), p === 'campaign' ? err.error : liveMessage(p)],
          confirmLabel: kind === 'rollback' ? 'Roll back' : 'Publish',
        });
        return;
      case 'drift': {
        if (typeof err.body.etag === 'string') etagRef.current = err.body.etag;
        const d = await loadDetail(currentId as string, false);
        const drift: TemplateDrift | null = (d?.template.drift as TemplateDrift) ?? err.body.drift ?? null;
        // The drift banner and the confirmation say it; a notice as well would repeat it.
        if (drift) openOverwrite(kind === 'rollback' ? 'rollback' : 'publish', drift, opts);
        else setNotice({ severity: 'warning', text: err.error });
        return;
      }
      case 'rollback_target_edited': {
        // The previous version changed in the console after SIMS saved it. The server captured
        // the edit into history; it goes live only once this exact edit is confirmed.
        if (typeof err.body.etag === 'string') etagRef.current = err.body.etag;
        const t = err.body.target ?? {};
        void reload(false);
        setConfirm({
          kind: 'rollback', title: `Roll back to the edited v${t.versionNo ?? '?'}?`,
          lines: [
            `The previous version was edited in the SendGrid console after SIMS saved it. That edit is saved as `
              + `v${t.versionNo ?? '?'} in history, and it is what would go live. View it in history first if `
              + 'you are not sure.',
          ],
          confirmLabel: 'Roll back to the edit',
          opts: { ...opts, confirmTargetEdited: { sgVersionId: t.sgVersionId ?? null, sgUpdatedAt: t.sgUpdatedAt ?? null } },
        });
        return;
      }
      case 'stale':
        setNotice({ severity: 'warning', action: 'reload',
          text: 'Someone else changed this template since you opened it. Reload to see the change; what you '
            + 'typed stays in the editor. Then publish again if you still mean to.' });
        return;
      case 'op_pending':
      case 'needs_reconcile':
      case 'not_created':
        setNotice({ severity: 'warning', text: err.error, action: 'reconcile' });
        void reload();
        return;
      case 'templates_key_missing':
        setKeyMissing(true);
        setNotice({ severity: 'error', text: err.error });
        return;
      default: {
        const wait = err.retryAfter ? ` Try again in about ${err.retryAfter} seconds.` : '';
        setNotice({ severity: 'error', text: `${err.error}${wait}` });
        if (err.code === 'missing_in_sendgrid' || err.code === 'archived') void reload();
      }
    }
  };

  const runWrite = async (kind: WriteKind, opts: WriteOpts = {}) => {
    setConfirm(null);
    setBusy(kind);
    setNotice(null);
    setWriteErrors([]);
    setWriteWarnings([]);
    try {
      const draft = currentDraft();
      let r: TemplateWriteResult;
      if (kind === 'create') {
        r = await createEmailTemplate({ ...draft, name: name.trim() });
      } else if (kind === 'publish') {
        r = await publishEmailTemplate(currentId as string, {
          subject: draft.subject, html: draft.html, ...(draft.plain ? { plain: draft.plain } : {}),
          etag: etagRef.current, ...opts,
        });
      } else {
        r = await rollbackEmailTemplate(currentId as string, { etag: etagRef.current, ...opts });
      }
      const id = r.template?.id || currentId;
      if (r.code === 'may_be_live') {
        if (kind === 'create' && id) setCurrentId(id);
        setNotice({ severity: 'warning', text: r.error || 'SendGrid did not answer in time. The change may already be live. Press Check now.', action: 'reconcile' });
        if (id) await loadDetail(id, false);
        onChanged('SendGrid did not confirm the change yet.');
        return;
      }
      setWriteWarnings(Array.isArray(r.warnings) ? r.warnings : []);
      if (kind === 'create') {
        setCurrentId(id);
        setBaseline(draft.purpose === 'campaign'
          ? { subject: draft.subject, html: draft.html, plain: draft.plain ?? '' }
          : { subject: draft.subject, html: draft.html, plain: '' });
        setNotice({ severity: 'success', text: 'Created in SendGrid. Version 1 is live.' });
        if (id) await loadDetail(id, false);
        onChanged('Template created.');
      } else if (kind === 'publish') {
        setBaseline({ subject: draft.subject, html: draft.html, plain: draft.plain ?? '' });
        const n = r.version?.versionNo;
        setNotice({ severity: 'success',
          text: `Published${n ? ` v${n}` : ''}. It is live in SendGrid; the version before it is kept for roll back.` });
        await reload(false);
        onChanged('Template published.');
      } else {
        const wasDirty = dirtyNow();
        setNotice({ severity: 'success', text: `Rolled back. v${r.template?.activeVersionNo ?? '?'} is live again.`
          + (wasDirty ? ' What you were editing is still in the editor.' : '') });
        // Show what is live now, unless that would throw away unpublished typing.
        await reload(!wasDirty);
        onChanged('Template rolled back.');
      }
    } catch (e) {
      const err = toTemplateApiError(e, 'The change could not be made.');
      // A definite SendGrid failure still rewrites the template (the claim is released), and a
      // create may already have made the SendGrid template. Take the server's copy, or the next
      // attempt is refused as stale - or, for a create, makes a second empty SendGrid template.
      const t = err.body.template as TemplatePublic | undefined;
      if (t?.etag) etagRef.current = t.etag;
      if (kind === 'create' && t?.id) {
        setCurrentId(t.id);
        await loadDetail(t.id, false);          // what was typed stays in the editor
        setNotice({ severity: 'warning', text: err.code === 'draft_created' ? err.error
          : `The template was made in SendGrid but its first version was not saved (${err.error}) `
            + 'Press Publish to finish it; do not create it again.' });
        onChanged('The template was made in SendGrid without a live version.');
        return;
      }
      if (t && (kind === 'publish' || kind === 'rollback')) void reload(false);
      await handleWriteError(kind, opts, err);
    } finally {
      setBusy(null);
    }
  };

  const openOverwrite = (kind: 'publish' | 'rollback', drift: TemplateDrift, opts: WriteOpts = {}) => {
    const usage = detail?.usage;
    const lines = [
      drift.kind === 'activated_other'
        ? `In the SendGrid console someone made a different version live${drift.versionNo ? ` (v${drift.versionNo} in history)` : ''}.`
        : `This template was edited in the SendGrid console${drift.detectedAt ? ` (noticed ${formatWhen(drift.detectedAt)})` : ''}. `
          + `That edit is saved as v${drift.versionNo ?? '?'} in history.`,
      kind === 'rollback'
        ? 'Rolling back replaces the console\'s version in SendGrid. The console version stays in history.'
        : 'Publishing replaces the console\'s version in SendGrid with what is in the editor. The console '
          + 'version stays in history and becomes the roll-back target.',
    ];
    const held = template?.activeVersionNo;
    if (drift.versionNo != null && held != null && held !== drift.versionNo) {
      lines.push(`The editor opened with v${held}, SIMS's last publish, not the console's v${drift.versionNo}. To keep `
        + `the console change, Cancel, View v${drift.versionNo} in history and Load it into the editor.`);
    }
    if (usage?.requiresConfirm || opts.confirmLive) lines.push(describeUsage(usage), liveMessage(purposeRef.current));
    setConfirm({
      kind, title: 'Replace the version from the SendGrid console?', lines,
      confirmLabel: kind === 'rollback' ? 'Roll back over it' : 'Publish over it',
      opts: { ...opts, confirmLive: opts.confirmLive || !!usage?.requiresConfirm || undefined,
        overwriteDrift: { sgVersionId: drift.sgVersionId, sgUpdatedAt: drift.sgUpdatedAt } },
    });
  };

  const startPublish = () => {
    if (creating) { void runWrite('create'); return; }
    if (!template) return;
    const usage = detail?.usage;
    const lines: string[] = [];
    const opts: WriteOpts = {};
    if (usage?.requiresConfirm) {
      lines.push(...confirmLinesForUsage(template.purpose, describeUsage(usage)));
      opts.confirmLive = true;
    }
    if (template.origin === 'adopted') lines.push(ADOPTED_NOTE);
    if (!lines.length) { void runWrite('publish', opts); return; }
    setConfirm({ kind: 'publish', opts, title: 'Publish a new version?', lines, confirmLabel: 'Publish' });
  };

  const startRollback = () => {
    if (!template) return;
    const usage = detail?.usage;
    const lines = [
      `v${template.previousVersionNo ?? '?'} becomes the live version in SendGrid again. `
        + `v${template.activeVersionNo ?? '?'} stays in history and becomes the roll-back target.`,
    ];
    const opts: WriteOpts = {};
    if (usage?.requiresConfirm) {
      lines.push(describeUsage(usage), liveMessage(template.purpose));
      opts.confirmLive = true;
    }
    setConfirm({ kind: 'rollback', opts, title: `Roll back to v${template.previousVersionNo ?? '?'}?`, lines,
      confirmLabel: 'Roll back' });
  };

  const checkNow = async () => {
    if (!currentId) return;
    setBusy('reconcile');
    setNotice(null);
    try {
      const r = await reconcileEmailTemplate(currentId);
      if (r.state === 'not_applied' && r.template?.archivedReason === 'create_not_applied') {
        // The template never reached SendGrid. The work is still in the editor: offer Create again.
        setCurrentId(null);
        setDetail(null);
        setName(r.template.name || name);
        setNotice({ severity: 'info', text: 'The template never reached SendGrid, so nothing went live. What you '
          + 'wrote is still here; press Create to try again.' });
        onChanged('The template was never created in SendGrid.');
        return;
      }
      const text = r.state === 'committed'
        ? 'SendGrid confirmed the change. It is live.'
        : r.state === 'not_applied'
          ? 'The change never reached SendGrid, so nothing went live. What you typed is still in the editor; '
            + 'publish again when ready.'
          : r.state === 'superseded'
            ? 'SendGrid has the new version, but someone made the previous version live again in the SendGrid '
              + 'console, and SIMS left it that way. What you typed is still in the editor; publish again if you '
              + 'still mean to replace it.'
            : 'Nothing was waiting for SendGrid.';
      setNotice({ severity: r.state === 'committed' ? 'success' : 'info', text });
      await reload(false);
      onChanged(text);
    } catch (e) {
      const err = toTemplateApiError(e, 'SIMS could not check SendGrid.');
      let text = err.error;
      if (err.code === 'too_soon' && err.retryAfter) text += ` (about ${err.retryAfter} seconds)`;
      if (err.code === 'ambiguous' && Array.isArray(err.body.ids)) text += ` SendGrid ids: ${err.body.ids.join(', ')}.`;
      if (err.code === 'templates_key_missing') setKeyMissing(true);
      setNotice({ severity: err.code === 'too_soon' || err.code === 'busy' ? 'info' : 'warning', text });
      if (err.code === 'drift' || err.code === 'stale') await reload(false);
    } finally {
      setBusy(null);
    }
  };

  const runArchive = async (archive: boolean) => {
    if (!currentId) return;
    setConfirm(null);
    setBusy('archive');
    setNotice(null);
    try {
      await archiveEmailTemplate(currentId, archive, etagRef.current);
      const text = archive
        ? 'Archived. It is still in SendGrid and still sendable; SIMS no longer lists it.'
        : 'Restored from the archive.';
      setNotice({ severity: 'success', text });
      await reload(false);
      onChanged(text);
    } catch (e) {
      const err = toTemplateApiError(e, 'The template could not be archived.');
      let text = err.error;
      if (err.code === 'in_use' && err.body.usage) text += ` ${describeUsage(err.body.usage)}`;
      if (err.code === 'templates_key_missing') setKeyMissing(true);
      setNotice({ severity: 'error', text, action: err.code === 'stale' ? 'reload'
        : err.code === 'op_pending' ? 'reconcile' : undefined });
    } finally {
      setBusy(null);
    }
  };

  const runTestSend = async () => {
    setBusy('test');
    setTestResult(null);
    try {
      const r = testMode === 'live' && currentId
        ? await sendTemplateTest({ mode: 'live', templateId: currentId })
        : await sendTemplateTest({ mode: 'draft', ...currentDraft() });
      setTestResult({ ok: true, text: `Sent to ${r.to}. SendGrid accepted it (${r.statusCode}). `
        + `${r.sentInLastHour} of ${r.limitPerHour} test sends used this hour.` });
    } catch (e) {
      const err = toTemplateApiError(e, 'The test could not be sent.');
      const problems = Array.isArray(err.body.errors) && err.body.errors.length
        ? ` ${err.body.errors.slice(0, 5).map(issueText).join(' · ')}` : '';
      // SendGrid's own reason for a refusal (the server caps it at 300 characters; never recipient data).
      const said = typeof err.body.detail === 'string' && err.body.detail.trim() ? ` ${err.body.detail.trim()}` : '';
      setTestResult({ ok: false, text: `${err.error}${said}${problems}` });
    } finally {
      setBusy(null);
    }
  };

  // ── history ───────────────────────────────────────────────────────────────────────────────────

  const viewVersion = async (versionNo: number | null) => {
    if (!currentId || versionNo == null) return;
    setVersionLoading(versionNo);
    try {
      setVersionView(await getEmailTemplateVersion(currentId, versionNo));
    } catch (e) {
      setNotice({ severity: 'error', text: toTemplateApiError(e, 'That version could not be loaded.').error });
    } finally {
      setVersionLoading(null);
    }
  };

  const loadVersionIntoEditor = (v: VersionDetail) => {
    const content: Content = {
      subject: v.subject || '', html: v.html || '',
      // A generated plain part stays generated; a typed one comes back as typed.
      plain: purposeRef.current === 'campaign' && !v.plainGenerated ? (v.plain || '') : '',
    };
    setVersionView(null);
    if (dirtyNow()) {
      setConfirm({ kind: 'replace', title: `Load v${v.versionNo} into the editor?`, confirmLabel: 'Replace',
        lines: ['What is in the editor now has not been published and will be replaced.'], content });
    } else {
      putContent(content, false);
      setNotice({ severity: 'info', text: `v${v.versionNo} is in the editor. Publish to make it live again as a new version.` });
    }
  };

  const insertToken = (token: string) => {
    if (lastField === 'html') { htmlRef.current?.insertAtCursor(token); return; }
    if (lastField === 'plain') { plainRef.current?.insertAtCursor(token); return; }
    const el = subjectInputRef.current;
    const start = el?.selectionStart ?? subject.length;
    const end = el?.selectionEnd ?? start;
    setSubject(subject.slice(0, start) + token + subject.slice(end));
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const requestClose = () => {
    if (busy) return;
    if (contentReady && dirtyNow()) setConfirmClose(true);
    else onClose();
  };

  // ── render ────────────────────────────────────────────────────────────────────────────────────

  const sync = template ? syncLabel(template) : null;
  const usage = detail?.usage ?? null;
  const drift = template?.syncState === 'drift' ? template.drift : null;
  const problems = writeErrors.length ? writeErrors : (preview?.errors ?? []);
  const warnings = [...(preview?.warnings ?? []), ...writeWarnings]
    .filter((w, i, all) => all.findIndex((x) => x.code === w.code && x.message === w.message) === i);
  const publishLabel = creating ? 'Create in SendGrid' : 'Publish';
  const publishBlockedWhy = !mayUse ? 'You do not have permission to change this kind of template.'
    : !keyReady ? 'Email template editing is not set up yet.'
      : archived ? 'Archived templates cannot be published. Restore it first.'
        : pendingOp ? 'SendGrid has not confirmed the last change. Press Check now first.'
          : !creating && !template?.sgTemplateId ? 'This template is not in SendGrid yet. Press Check now.'
            : !contentReady ? 'Loading…' : null;
  const fieldName: Record<Field, string> = { subject: 'subject', html: 'HTML', plain: 'plain text' };
  const purposeOptions = (Object.keys(catalog?.purposes ?? {}) as Purpose[]);
  const liveTestPossible = !!template && template.status === 'active' && !!template.activeVersionNo;

  return (
    <Dialog open={open} onClose={requestClose} fullScreen
            PaperProps={{ sx: { backgroundColor: 'var(--bg-primary, #fff)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', pb: 1 }}>
        <Box sx={{ ...SHRINKS, flex: '1 1 320px' }}>
          <Typography variant="h6" component="div" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
            {creating ? 'New email template' : (template?.name || 'Email template')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
            <Chip size="small" label={PURPOSE_LABEL[purpose]} />
            {template && (
              <Chip size="small" label={statusLabel(template)} color={STATUS_COLOR[template.status] ?? 'default'} />
            )}
            {sync && (
              <Tooltip title={sync.help}><Chip size="small" label={sync.label} color={sync.color} variant="outlined" /></Tooltip>
            )}
            {template?.origin === 'adopted' && <Chip size="small" label="Adopted" variant="outlined" />}
            {template?.isDripDefault && <Chip size="small" label="Drip default" color="info" variant="outlined" />}
            {template?.sgTemplateId && (
              <Box sx={{ display: 'flex', alignItems: 'center', ...SHRINKS }}>
                <Typography variant="caption" sx={{ ...MONO, color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                  {template.sgTemplateId}
                </Typography>
                <Tooltip title="Copy the SendGrid id">
                  <IconButton size="small" onClick={() => copy(template.sgTemplateId)} aria-label="Copy the SendGrid id">
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
        {!creating && (
          <Tooltip title="Reload from SIMS and check SendGrid again. What you typed stays.">
            <span>
              {/* With content: if the first load failed the fields are still empty and disabled, so
                  this is the retry; once content is in, typing is never overwritten. */}
              <IconButton onClick={() => void reload(!contentReady)} disabled={loading || !!busy} aria-label="Reload">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton onClick={requestClose} disabled={!!busy} aria-label="Close the editor">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {(loading || !!busy) && <LinearProgress />}

      <DialogContent dividers sx={{ p: 2, [ULTRAWIDE]: { p: 3 } }}>
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        {!keyReady && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Email template editing is not set up yet. An administrator must add the SendGrid template key.
            You can still check and preview here{setup.mailKeyConfigured ? ', and send a test of a draft to yourself' : ''}.
          </Alert>
        )}

        <Box sx={LAYOUT}>
          {/* ── placeholders for this purpose ── */}
          <Paper variant="outlined" sx={{ ...SHRINKS, gridArea: 'catalog', p: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)' }}>
              Placeholders · {PURPOSE_LABEL[purpose]}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', mb: 1 }}>
              Click one to insert it at the cursor in the {fieldName[lastField]}. Only these are filled in; any
              other name is refused when you check.
            </Typography>
            {!purposeCatalog && (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                The placeholder list could not be loaded.
              </Typography>
            )}
            <Box sx={KEY_LIST}>
              {(purposeCatalog?.keys ?? []).map((k) => {
                // A campaign's subject and plain text are not HTML, so SendGrid's escaping would show
                // ' and & as entities there: three braces, as the server's own warning asks. The drip
                // layout's subject key is left as it is (decision 10).
                const raw = k.raw || (purpose === 'campaign' && (lastField === 'subject' || lastField === 'plain'));
                const token = raw ? `{{{${k.name}}}}` : `{{${k.name}}}`;
                const allowed = k.contexts.includes(lastField) && !(lastField === 'plain' && purpose === 'drip_layout');
                return (
                  <Box key={k.name} sx={KEY_ROW}>
                    <Tooltip title={
                      <span>
                        {k.description}<br />
                        Allowed in: {k.contexts.join(', ')}
                        {k.sample ? <><br />Sample: {k.sample}</> : null}
                        {!allowed ? <><br />Not allowed in the {fieldName[lastField]}.</> : null}
                      </span>
                    }>
                      <span>
                        <Chip size="small" label={token} onClick={() => insertToken(token)}
                              disabled={!allowed || !contentReady}
                              sx={{ ...MONO, fontSize: '0.75rem' }} />
                      </span>
                    </Tooltip>
                    <Typography variant="caption" sx={KEY_DESC}>{k.description}</Typography>
                  </Box>
                );
              })}
              {(purposeCatalog?.unsubscribeTags ?? []).map((tag) => (
                <Box key={tag} sx={KEY_ROW}>
                  <Tooltip title="SendGrid fills this from the drip unsubscribe group. HTML only.">
                    <span>
                      <Chip size="small" label={tag} variant="outlined"
                            disabled={lastField !== 'html' || !contentReady}
                            onClick={() => insertToken(tag)} sx={{ ...MONO, fontSize: '0.75rem' }} />
                    </span>
                  </Tooltip>
                  <Typography variant="caption" sx={KEY_DESC}>Unsubscribe link for the drip group.</Typography>
                </Box>
              ))}
            </Box>
            <Button size="small" onClick={() => setShowRules(!showRules)} sx={{ mt: 1, color: 'var(--text-secondary)' }}
                    startIcon={showRules ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
              Rules and allowed syntax
            </Button>
            <Collapse in={showRules} unmountOnExit>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                {(purposeCatalog?.rules ?? []).map((r) => <li key={r}>{r}</li>)}
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--text-secondary)' }}>
                Allowed: {(catalog?.syntax.allowed ?? []).join(' · ')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--text-secondary)' }}>
                Refused by name: {(catalog?.syntax.refused ?? []).join(', ')}
              </Typography>
            </Collapse>
          </Paper>

          {/* ── the fields ── */}
          <Box sx={{ ...SHRINKS, gridArea: 'editor', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {notice && (
              <Alert severity={notice.severity} action={
                notice.action === 'reconcile' ? (
                  <Button color="inherit" size="small" onClick={() => void checkNow()} disabled={!!busy || !keyReady}>
                    Check now
                  </Button>
                ) : notice.action === 'reload' ? (
                  <Button color="inherit" size="small" onClick={() => { setNotice(null); void reload(false); }} disabled={!!busy}>
                    Reload
                  </Button>
                ) : undefined
              }>
                {notice.text}
              </Alert>
            )}
            {pendingOp && (
              <Alert severity="warning" action={
                <Button color="inherit" size="small" onClick={() => void checkNow()} disabled={!!busy || !keyReady}
                        startIcon={<SyncIcon />}>
                  Check now
                </Button>
              }>
                {pendingOp.uncertain
                  ? 'SendGrid did not confirm the last change. It may already be live.'
                  : 'A change to this template has not been confirmed by SendGrid yet.'}
                {pendingOp.startedBy ? ` Started by ${pendingOp.startedBy} ${formatWhen(pendingOp.startedAt)}.` : ''}
              </Alert>
            )}
            {drift && (drift.kind === 'edited' || drift.kind === 'activated_other') && (
              <Alert severity="warning" action={
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {drift.versionNo != null && (
                    <Button color="inherit" size="small" onClick={() => void viewVersion(drift.versionNo)}>View</Button>
                  )}
                  <Button color="inherit" size="small" disabled={!!publishBlockedWhy || !!busy}
                          onClick={() => openOverwrite('publish', drift)}>
                    Publish over it
                  </Button>
                </Box>
              }>
                {drift.kind === 'edited'
                  ? `Changed in the SendGrid console${drift.detectedAt ? ` (noticed ${formatWhen(drift.detectedAt)})` : ''}; `
                    + `saved as v${drift.versionNo ?? '?'} in history. That version is what SendGrid sends now.`
                  : `A different version${drift.versionNo != null ? ` (v${drift.versionNo})` : ''} was made live in the `
                    + `SendGrid console${drift.detectedAt ? ` (noticed ${formatWhen(drift.detectedAt)})` : ''}.`}
                {drift.versionNo != null && template?.activeVersionNo != null && template.activeVersionNo !== drift.versionNo
                  ? ` The editor opened with v${template.activeVersionNo}, SIMS's last publish, not the console's version: `
                    + `View v${drift.versionNo} and Load it into the editor to build on it.`
                  : ''}
              </Alert>
            )}
            {template?.syncState === 'missing' && (
              <Alert severity="error">
                This template no longer exists in SendGrid. Its history is still here. Archive it, or create a new
                template from one of its versions.
              </Alert>
            )}
            {template?.versionsMissing && (
              <Alert severity="info">
                The previous version is no longer in SendGrid, so Roll back is off. Load an older version from
                history and publish it instead.
              </Alert>
            )}
            {template?.origin === 'adopted' && !archived && <Alert severity="info">{ADOPTED_NOTE}</Alert>}
            {template?.needsFix && (template.issues ?? []).length > 0 && (
              <Alert severity="warning">
                What is live now breaks these SIMS rules; the next publish must fix them:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {template.issues.slice(0, 10).map((i, n) => <li key={n}>{issueText(i)}</li>)}
                </Box>
              </Alert>
            )}
            {template?.lastError?.message && !pendingOp && (
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                Last SendGrid error {formatWhen(template.lastError.at)}: {template.lastError.message}
              </Typography>
            )}
            {detail?.driftCheck.ok === false && (
              <Typography variant="caption" sx={{ color: 'var(--warning, #b26a00)' }}>
                SIMS could not check SendGrid for console edits just now ({detail.driftCheck.reason}). Publishing
                checks again first.
              </Typography>
            )}
            {contentNote && <Alert severity="info">{contentNote}</Alert>}

            {creating && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Name" value={name} onChange={(e) => setName(e.target.value)}
                  inputProps={{ maxLength: limits?.nameChars ?? 75 }}
                  helperText={`${name.trim().length}/${limits?.nameChars ?? 75}. Staff see this, and so does anyone `
                    + 'with SendGrid console access: never a person\'s name or address.'}
                  sx={{ ...SHRINKS, flex: '2 1 320px' }}
                  error={writeErrors.some((e) => e.code.startsWith('name'))}
                  autoFocus
                />
                <TextField
                  select label="Used for" value={purpose}
                  onChange={(e) => {
                    const p = e.target.value as Purpose;
                    setPurpose(p);
                    purposeRef.current = p;
                    if (p === 'drip_layout') plainRef.current?.setValue('');
                  }}
                  helperText={purpose === 'campaign' ? 'Campaign Portal email and survey pickers.'
                    : 'The frame every drip email is sent in.'}
                  sx={{ ...SHRINKS, flex: '1 1 220px' }}
                >
                  {purposeOptions.map((p) => <MenuItem key={p} value={p}>{PURPOSE_LABEL[p]}</MenuItem>)}
                </TextField>
              </Box>
            )}

            <TextField
              label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              inputRef={subjectInputRef} onFocus={() => setLastField('subject')}
              inputProps={{ maxLength: limits?.subjectChars ?? 200, spellCheck: false }}
              InputLabelProps={{ shrink: true }} fullWidth disabled={!contentReady}
              helperText={`${subject.trim().length}/${limits?.subjectChars ?? 200}. `
                + (purpose === 'campaign'
                  ? 'This subject replaces the campaign\'s own subject when SendGrid sends with this template.'
                  : 'Must use the subject placeholder: the entry\'s own subject is filled in.')}
            />
            <CodeArea
              ref={htmlRef} label="HTML" disabled={!contentReady}
              onChange={setHtmlText} onFocus={() => setLastField('html')} sx={HTML_AREA}
              helperText={`${formatBytes(htmlBytes)} of ${formatBytes(limits?.htmlBytes ?? 307200)}`
                + (limits && htmlBytes > limits.gmailClipBytes ? '. Gmail clips messages this large.' : '.')
                + ' Scripts, forms, frames, event handlers and javascript: links are refused by name.'}
            />
            {purpose === 'campaign' ? (
              <CodeArea
                ref={plainRef} label="Plain text (optional)" disabled={!contentReady}
                placeholder="Leave blank and SIMS generates the plain text from the HTML."
                onChange={setPlainText} onFocus={() => setLastField('plain')} sx={PLAIN_AREA}
                helperText={plainText.trim()
                  ? `${formatBytes(plainBytes)} of ${formatBytes(limits?.plainBytes ?? 102400)}. Sent exactly as written.`
                  : 'Blank: generated from the HTML on every publish.'}
              />
            ) : (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                The plain-text part of a drip layout is generated by SIMS from the HTML, with the message as plain
                text in place of {'{{{body}}}'}. The preview shows it.
              </Typography>
            )}

            {problems.length > 0 && (
              <Alert severity="error">
                {writeErrors.length ? 'SIMS refused to save:' : 'Fix these before publishing:'}
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {problems.slice(0, 30).map((i, n) => <li key={n}>{issueText(i)}</li>)}
                </Box>
                {problems.length > 30 && `…and ${problems.length - 30} more.`}
              </Alert>
            )}
            {warnings.length > 0 && (
              <Alert severity="warning">
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {warnings.slice(0, 20).map((i, n) => <li key={n}>{issueText(i)}</li>)}
                </Box>
              </Alert>
            )}
          </Box>

          {/* ── preview and test send ── */}
          <Paper variant="outlined" sx={{ ...SHRINKS, gridArea: 'preview', p: 2, [ULTRAWIDE]: { p: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                Preview with sample data
              </Typography>
              {previewing && <CircularProgress size={16} />}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                      disabled={previewing || !contentReady} onClick={() => void runPreview(true)}>
                Check &amp; preview
              </Button>
              <FormControlLabel
                control={<Switch size="small" checked={autoPreview} onChange={(e) => setAutoPreview(e.target.checked)} />}
                label={<Typography variant="caption">Live</Typography>}
              />
              <ToggleButtonGroup size="small" exclusive value={frameWidth}
                                 onChange={(_e, v) => v && setFrameWidth(v)} aria-label="Preview width">
                <ToggleButton value={620}>Desktop 620</ToggleButton>
                <ToggleButton value={375}>Phone 375</ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Off: remote images are blocked, so the image hosts do not see your address.">
                <FormControlLabel
                  control={<Switch size="small" checked={loadImages} onChange={(e) => setLoadImages(e.target.checked)} />}
                  label={<Typography variant="caption">Images</Typography>}
                />
              </Tooltip>
            </Box>

            {autoPreviewPaused && (
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--text-secondary)' }}>
                Live preview pauses above {formatBytes(AUTO_PREVIEW_MAX_BYTES)} of HTML; press Check &amp; preview.
              </Typography>
            )}
            {previewError && <Alert severity="error" sx={{ mb: 1 }}>{previewError}</Alert>}
            {preview && !preview.ok && rendered && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This is the last preview that passed. Fix the problems listed with the fields to update it.
              </Alert>
            )}

            {rendered ? (
              <>
                {/* Shown as text on purpose: an entity SendGrid would put in the subject shows as typed. */}
                <Typography variant="body2" sx={{ color: 'var(--text-primary)', mb: 1, overflowWrap: 'anywhere' }}>
                  <strong>{renderedFromStore ? 'Subject as written:' : 'Subject:'}</strong> {rendered.subject || '—'}
                </Typography>
                {renderedFromStore && (
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--text-secondary)' }}>
                    {drift?.versionNo != null && template?.activeVersionNo !== drift.versionNo
                      ? `SIMS's last publish (v${template?.activeVersionNo ?? '?'}); SendGrid is sending v${drift.versionNo} `
                        + 'from the console.'
                      : 'The live version as SIMS stored it.'} Check &amp; preview renders what is in the editor.
                  </Typography>
                )}
                <Box sx={{ backgroundColor: '#e9edf3', borderRadius: 1, p: 1, display: 'flex', justifyContent: 'center' }}>
                  <Box
                    component="iframe"
                    title="Email template preview"
                    // No allow-scripts, no allow-same-origin, no allow-popups: the document cannot run
                    // anything or reach back into SIMS. The server put a CSP meta after <head>.
                    sandbox=""
                    referrerPolicy="no-referrer"
                    srcDoc={rendered.previewDocument}
                    sx={{ ...FRAME_HEIGHT, width: frameWidth, maxWidth: '100%', border: 0, display: 'block',
                      backgroundColor: '#ffffff' }}
                  />
                </Box>
                {rendered.plain && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, color: 'var(--text-primary)' }}>
                      Plain-text part
                    </Typography>
                    <Box component="pre" sx={{ ...MONO, m: 0, p: 1.5, fontSize: 12, whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere', maxHeight: 280, overflow: 'auto', border: '1px solid var(--card-border)',
                      borderRadius: 1, color: 'var(--text-primary)' }}>
                      {rendered.plain}
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Box sx={{ ...FRAME_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px dashed var(--card-border)', borderRadius: 1, maxHeight: 320 }}>
                <Typography variant="body2" sx={{ color: 'var(--text-secondary)', textAlign: 'center', px: 2 }}>
                  {contentReady ? 'Write the subject and HTML; the preview appears here.' : 'Loading…'}
                </Typography>
              </Box>
            )}
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'var(--text-secondary)' }}>
              {RESIDUAL_NOTE} The sample person is Testy McTestface; no real student or lead is used.
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* A real send, to the signed-in user only. The server takes the address from the verified
                token, enforces the staff domain and the hourly cap, and audits it. */}
            <Typography variant="subtitle2" sx={{ color: 'var(--text-primary)', mb: 0.5 }}>Test send</Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <ToggleButtonGroup size="small" exclusive value={testMode} onChange={(_e, v) => v && setTestMode(v)}>
                <ToggleButton value="draft">This draft</ToggleButton>
                <ToggleButton value="live" disabled={!liveTestPossible}>Live template</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" startIcon={<SendIcon />} onClick={() => void runTestSend()}
                      disabled={!!busy || !mayUse || !setup.mailKeyConfigured || !contentReady
                        || (testMode === 'live' && !liveTestPossible)}>
                {busy === 'test' ? 'Sending…' : `Send to me${callerEmail ? ` (${callerEmail})` : ''}`}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--text-secondary)' }}>
              {!setup.mailKeyConfigured ? 'Test sends need SendGrid sending configured (SENDGRID_API_KEY).'
                : testMode === 'draft'
                  ? 'Sends what is in the editor, rendered with the sample data, with [TEST] before the subject.'
                  : 'Sends through SendGrid with the live template, exactly as SendGrid renders it.'}
              {' '}Only to your own staff address; ten an hour.
            </Typography>
            {testResult && <Alert severity={testResult.ok ? 'success' : 'error'} sx={{ mt: 1 }}>{testResult.text}</Alert>}
          </Paper>

          {/* ── usage and history ── */}
          <Paper variant="outlined" sx={{ ...SHRINKS, gridArea: 'history', p: 2 }}>
            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>History</Typography>
            {creating ? (
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                History starts when the template is created in SendGrid.
              </Typography>
            ) : (
              <>
                {usage && (
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                    {describeUsage(usage)}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  <Tooltip title={!template?.hasRollback ? 'There is no earlier version in SendGrid to go back to.'
                    : publishBlockedWhy || ''}>
                    <span>
                      <Button size="small" variant="outlined" startIcon={<UndoIcon />}
                              disabled={!template?.hasRollback || !!publishBlockedWhy || !!busy}
                              onClick={startRollback}>
                        {template?.previousVersionNo ? `Roll back to v${template.previousVersionNo}` : 'Roll back'}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                {(detail?.versions ?? []).length === 0 && (
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>No versions yet.</Typography>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(detail?.versions ?? []).map((v: VersionSummary) => (
                    <Box key={`${v.versionNo}-${v.sgVersionId}`}
                         sx={{ ...SHRINKS, p: 1, border: '1px solid var(--card-border)', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          v{v.versionNo ?? '?'}
                        </Typography>
                        <Chip size="small" variant="outlined"
                              label={v.source ? VERSION_SOURCE_LABEL[v.source] ?? v.source : 'Unknown'} />
                        {drift ? (
                          <>
                            {v.isLiveInSendGrid && <Chip size="small" color="warning" label="Live in SendGrid (console)" />}
                            {v.isActive && !v.isLiveInSendGrid && <Chip size="small" variant="outlined" label="Last SIMS publish" />}
                          </>
                        ) : v.isActive && <Chip size="small" color="success" label="Live" />}
                        {v.isPrevious && <Chip size="small" color="info" variant="outlined" label="Roll-back target" />}
                        {v.state === 'pending' && <Chip size="small" color="warning" variant="outlined" label="Not confirmed" />}
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" disabled={versionLoading != null || !v.contentStored}
                                onClick={() => void viewVersion(v.versionNo)}>
                          {versionLoading === v.versionNo ? 'Loading…' : 'View'}
                        </Button>
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                        {formatWhen(v.publishedAt || v.createdAt)}{v.createdBy ? ` · ${v.createdBy}` : ''}
                        {!v.contentStored ? ' · too large to keep a copy' : ''}
                      </Typography>
                      {v.subject && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>
                          {v.subject}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        {!creating && template && (
          <Tooltip title={!mayUse ? 'You do not have permission to change this kind of template.'
            : !keyReady ? 'Email template editing is not set up yet.'
              : archived ? 'Show it in the list again. It stays as it is in SendGrid.'
                : 'SIMS stops listing it. It stays in SendGrid and stays sendable.'}>
            <span>
              <Button startIcon={archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                      disabled={!canWrite || !!pendingOp || template.archivedReason === 'create_not_applied'}
                      onClick={() => archived ? void runArchive(false) : setConfirm({
                        kind: 'archive', title: 'Archive this template?', confirmLabel: 'Archive',
                        lines: ['SIMS stops listing it. Nothing changes in SendGrid: the template stays there and stays '
                          + 'sendable. SIMS cannot delete templates; an administrator can delete it in the SendGrid console.'],
                      })}
                      sx={{ color: 'var(--text-secondary)' }}>
                {archived ? 'Restore' : 'Archive'}
              </Button>
            </span>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={requestClose} disabled={!!busy}>Close</Button>
        <Tooltip title={publishBlockedWhy || ''}>
          <span>
            <Button variant="contained" startIcon={<PublishIcon />} onClick={startPublish}
                    disabled={!canWrite || !!publishBlockedWhy}
                    sx={{ backgroundColor: 'var(--accent)' }}>
              {busy === 'create' || busy === 'publish' ? 'Publishing…' : publishLabel}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      {/* ── confirmations ── */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent>
          {(confirm?.lines ?? []).map((line, n) => (
            <DialogContentText key={n} sx={{ mb: 1.5 }}>{line}</DialogContentText>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained"
                  color={confirm?.opts?.overwriteDrift || confirm?.opts?.confirmTargetEdited ? 'warning' : 'primary'}
                  onClick={() => {
                    const c = confirm;
                    if (!c) return;
                    if (c.kind === 'archive') void runArchive(true);
                    else if (c.kind === 'replace') {
                      setConfirm(null);
                      if (c.content) putContent(c.content, false);
                    } else void runWrite(c.kind, c.opts);
                  }}>
            {confirm?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Close without publishing?</DialogTitle>
        <DialogContent>
          <DialogContentText>What you changed has not been published and will be lost.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="warning" onClick={() => { setConfirmClose(false); onClose(); }}>Discard</Button>
        </DialogActions>
      </Dialog>

      {/* ── one version from history ── */}
      <Dialog open={!!versionView} onClose={() => setVersionView(null)} maxWidth="md" fullWidth
              PaperProps={{ sx: { height: '92vh', maxHeight: '92vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          v{versionView?.versionNo}
          {versionView?.source && <Chip size="small" variant="outlined" label={VERSION_SOURCE_LABEL[versionView.source] ?? versionView.source} />}
          {drift ? (
            <>
              {versionView?.isLiveInSendGrid && <Chip size="small" color="warning" label="Live in SendGrid (console)" />}
              {versionView?.isActive && !versionView?.isLiveInSendGrid
                && <Chip size="small" variant="outlined" label="Last SIMS publish" />}
            </>
          ) : versionView?.isActive && <Chip size="small" color="success" label="Live" />}
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setVersionView(null)} aria-label="Close the version"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {versionView && (
            <>
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                {formatWhen(versionView.publishedAt || versionView.createdAt)}
                {versionView.createdBy ? ` · ${versionView.createdBy}` : ''}
                {versionView.note ? ` · ${versionView.note}` : ''}
                {versionView.sgEditor === 'design' ? ' · made in SendGrid\'s design editor' : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
                <strong>Subject as written:</strong> {versionView.subject || '—'}
              </Typography>
              {versionView.previewDocument ? (
                <Box
                  component="iframe" title="Version preview" sandbox="" referrerPolicy="no-referrer"
                  srcDoc={versionView.previewDocument}
                  sx={{ width: '100%', flex: '1 1 auto', minHeight: 420, border: '1px solid var(--card-border)',
                    display: 'block', backgroundColor: '#ffffff' }}
                />
              ) : (
                <Alert severity="info">SIMS has no copy of this version's content.</Alert>
              )}
              {versionView.plain && !versionView.plainGenerated && (
                <Box component="pre" sx={{ ...MONO, m: 0, p: 1.5, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 200,
                  overflow: 'auto', border: '1px solid var(--card-border)', borderRadius: 1, color: 'var(--text-primary)' }}>
                  {versionView.plain}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" sx={{ color: 'var(--text-secondary)', mr: 'auto', pl: 1 }}>
            Rendered with the sample data where it passes SIMS's rules; otherwise shown as stored.
          </Typography>
          <Button onClick={() => setVersionView(null)}>Close</Button>
          <Button variant="contained" disabled={!versionView?.contentStored || !versionView?.html}
                  onClick={() => versionView && loadVersionIntoEditor(versionView)}
                  sx={{ backgroundColor: 'var(--accent)' }}>
            Load into editor
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
